import { applyLiveIncrement, setStatusOverride } from '../counts';
import type { CountState } from '../counts';
import { ok, reject } from '../result';
import type { DomainResult } from '../result';
import { planUndoIncrement } from '../undo';
import type {
  CaptureOp,
  Decision,
  IncrementCommand,
  IncrementSnapshot,
  SetStatusOverrideCommand,
  SetStatusOverrideSnapshot,
  UndoIncrementCommand,
  UndoIncrementSnapshot,
} from './types';

/**
 * The capture commands — design §5.1, the hottest path in the app.
 *
 * ── Both handlers return `Decision<CaptureOp>`, and that is the load-bearing line ──
 *
 * Design §4, INV-4: *"Handler patch shapes are `Patch<table, allowedKeyUnion>`. The capture
 * handlers' key unions **exclude `boardPosition`**, so an increment or a status change **cannot
 * compile** a position write."* `CaptureOp` omits `setBoardPositions`, so that is now literally
 * true of this file: a position write here is a type error, not a review finding.
 *
 * It also discharges LOCK-5 without a test having to enumerate surfaces. *"A Discovery visit MUST
 * NOT itself change any tile's position or status"* holds because Discovery dispatches the **same**
 * `Increment` command as the locked board, and neither can express a position write.
 *
 * ── Why the count is computed here and not carried on the command ────────────
 *
 * §5.1 step 4 pre-builds the Observation row at `pointerdown` — the ULID, the derived match minute,
 * the whole row — which is what keeps the transaction to two tables and off the `matches` row. The
 * **count patch is deliberately not pre-built**: INV-2 requires `prev±1` to come from a row read
 * *inside* the transaction, or two taps drained back-to-back by the single-flight queue would both
 * compute from the same stale `cachedCount` and the second would silently lose an increment.
 *
 * ── `post: []` on Increment is a decision, not an omission ───────────────────
 *
 * §5.1 step 7: *"`Increment`'s postcondition list is deliberately **empty**; it is the hottest
 * command in the app and the invariants it could violate (INV-2) are enforced by construction rather
 * than by a check."* Construction here means: `cachedCount` moves only through `writeCountState`,
 * computed by `counts.ts` from an in-transaction read, in the same transaction as the Observation
 * insert. There is no window in which the two could disagree, so a postcondition would cost a read
 * on every tap to re-assert something that cannot be false.
 */

const MODULE = 'commands/capture';

/**
 * LIVE-2 / DISC-3 — one increment.
 *
 * The pre-built row is validated rather than trusted. It crosses a boundary from the UI, and three
 * of its fields decide which ledger row is written and which hypothesis is counted; a mismatch would
 * be a silent mis-attribution rather than a visible failure. Validation is four comparisons on a row
 * already in hand, which is nothing against a 60 ms real budget.
 */
export function handleIncrement(
  cmd: IncrementCommand,
  snap: IncrementSnapshot,
): Decision<CaptureOp> {
  const guard = requireOwnHypothesis(cmd.matchId, cmd.hypothesisId, snap);
  if (!guard.ok) return guard;

  const row = cmd.observation;
  if (row.kind !== 'increment') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'An Increment command may only carry an `increment` Observation.',
      { hypothesisId: cmd.hypothesisId, kind: row.kind },
    );
  }
  if (row.hypothesisId !== cmd.hypothesisId || row.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'The pre-built Observation names a different hypothesis or match than the command does.',
      {
        commandHypothesisId: cmd.hypothesisId,
        rowHypothesisId: row.hypothesisId,
        rowMatchId: row.matchId,
      },
    );
  }

  return ok({
    ops: [
      { t: 'insertObservation', row },
      // LIVE-11 — `applyLiveIncrement`, never `applyObservation`: a tap on a dismissed tile
      // un-dismisses it. The other function preserves the override and is replay's (INV-9); using
      // one for both would silently un-disprove every hypothesis on import.
      { t: 'writeCountState', id: cmd.hypothesisId, next: applyLiveIncrement(guard.value, row.createdAt) },
    ],
    log: [], // §5.1 — the logger never writes inside the tap transaction.
    post: [], // §5.1 step 7 — see the module note.
  });
}

/**
 * LIVE-6 — walk back one increment on one hypothesis (build unit U-10).
 *
 * ── The selection rule is U-2's, reused and not re-derived ───────────────────
 *
 * `planUndoIncrement` (`domain/undo.ts`) owns *which* increment and *what the hypothesis becomes*,
 * including §5.4's repeat-activation semantics: after each delete `lastIncrementTs` is rewritten to
 * the new newest, so each surviving increment is judged against **its own** window. Re-deriving any
 * of that here would be a second copy of a rule only one of which would be tested — and it is
 * precisely the rule whose three negatives LIVE-6 states.
 *
 * This handler contributes the two things the planner cannot see: the **snapshot read inside the
 * transaction** (the match's live state and the hypothesis's live count), and the mapping from a
 * plan to ops. Everything LIVE-6 forbids is enforced below it, at three independent layers:
 *
 * | LIVE-6 clause | Where it is enforced |
 * |---|---|
 * | never another hypothesis's increment | the command carries no observation id; `planUndoIncrement` re-filters on `hypothesisId`; `repos.deleteIncrement` re-reads the row inside the transaction and refuses a foreign one |
 * | never a `voiceNote` or `flag` | the `[matchId+hypothesisId+kind+createdAt]` range pins `kind`; the planner re-filters; the repo refuses a non-increment |
 * | never once state leaves in-match | `isInMatch(match.state)` — read **here**, from the transaction's own snapshot, so a Full Time that commits between the finger landing and this transaction closes the window |
 * | the window survives the Half tap | `isInMatch` includes `halftime`, so an increment at 44:58 stays undoable after the 45:00 hold |
 *
 * ── `now` comes from the snapshot, never from the command ────────────────────
 *
 * §5.4: *"The predicate is re-checked **inside the transaction** by the handler; the UI check is
 * only a render hint."* A command that sat in §5.2's single-flight queue until its window had closed
 * is therefore rejected rather than honoured, and the affordance being visible is never the thing
 * that decides.
 */
export function handleUndoIncrement(
  cmd: UndoIncrementCommand,
  snap: UndoIncrementSnapshot,
): Decision<CaptureOp> {
  if (snap.match === undefined) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_MATCH_NOT_FOUND',
      'That match no longer exists, so there is nothing to undo.',
      { hypothesisId: cmd.hypothesisId },
    );
  }

  const guard = requireOwnHypothesis(cmd.matchId, cmd.hypothesisId, { ...snap, hypothesis: snap.hypothesis });
  if (!guard.ok) return guard;

  const plan = planUndoIncrement({
    hypothesisId: cmd.hypothesisId,
    state: snap.match.state,
    now: snap.now,
    current: guard.value,
    candidates: snap.candidates,
  });
  // A rejection is already a `DomainResult` carrying its own OBS-1 entry (`reject()` is the only
  // constructor), so it is returned rather than re-wrapped — re-wrapping would either lose the code
  // the caller branches on or duplicate the log line.
  if (!plan.ok) return plan;

  return ok({
    ops: [
      // Order matters within the transaction only for readability — both land or neither does — but
      // the delete is written first so a reader sees the ledger change before the count that
      // describes it, which is the direction INV-2 is derived in.
      {
        t: 'deleteIncrement',
        matchId: cmd.matchId,
        hypothesisId: cmd.hypothesisId,
        observationId: plan.value.deleteObservationId,
      },
      { t: 'writeCountState', id: cmd.hypothesisId, next: plan.value.next },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'LIVE-6: one increment undone',
        data: {
          hypothesisId: cmd.hypothesisId,
          observationId: plan.value.deleteObservationId,
          cachedCount: plan.value.next.cachedCount,
        },
      },
    ],
    post: [],
  });
}

/**
 * LIVE-5 / HT-2 — set or clear the dismissal dimension (§3.3).
 *
 * `status` is re-derived by the one expression in `status.ts` inside `setStatusOverride`, so INV-3's
 * *"a hypothesis leaving either returns to `confirmed` or `tracking` per its current count"* is the
 * same code as auto-confirm and there is no branch to get wrong.
 *
 * INV-4 in its own words — *"`boardPosition` … is never changed by increments, status changes, or
 * Discovery visits"* — is why this handler shares `CaptureOp` with `handleIncrement` rather than
 * getting a wider union of its own.
 */
export function handleSetStatusOverride(
  cmd: SetStatusOverrideCommand,
  snap: SetStatusOverrideSnapshot,
): Decision<CaptureOp> {
  const guard = requireOwnHypothesis(cmd.matchId, cmd.hypothesisId, snap);
  if (!guard.ok) return guard;

  return ok({
    ops: [
      { t: 'writeCountState', id: cmd.hypothesisId, next: setStatusOverride(guard.value, cmd.statusOverride) },
    ],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: `status override set to ${cmd.statusOverride ?? 'null'}`,
        data: { hypothesisId: cmd.hypothesisId },
      },
    ],
    post: [],
  });
}

/**
 * The two rejections both capture handlers share: the row vanished, or it belongs to another match.
 *
 * The cross-match check is not defensive padding. `hypothesisId` is resolved from a DOM
 * `data-hid` at `pointerdown` (§5.1 step 1); a stale surface after a match change would otherwise
 * write a real increment onto a previous match's hypothesis, and nothing downstream would flag it
 * because every row involved is individually valid.
 */
function requireOwnHypothesis(
  matchId: string,
  hypothesisId: string,
  snap: IncrementSnapshot,
): DomainResult<CountState> {
  const hypothesis = snap.hypothesis;
  if (hypothesis === undefined) {
    return reject<CountState>(
      MODULE,
      matchId,
      'ERR_HYPOTHESIS_NOT_FOUND',
      'That hypothesis no longer exists.',
      { hypothesisId },
    );
  }
  if (hypothesis.matchId !== matchId) {
    return reject<CountState>(
      MODULE,
      matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That hypothesis belongs to a different match.',
      { hypothesisId, rowMatchId: hypothesis.matchId },
    );
  }
  return ok<CountState>({
    cachedCount: hypothesis.cachedCount,
    lastIncrementTs: hypothesis.lastIncrementTs,
    statusOverride: hypothesis.statusOverride,
    status: hypothesis.status,
  });
}
